import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Undo2 } from 'lucide-react';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { formatCount, formatTaka } from '@/utils/number';
import { PCMP_PRE_BATCH_LABELS } from '../../constants/pcmpMis.constants';
import { PCMP_MKT_COMMENT_MAX } from '../../constants/pcmpProposal.constants';
import { useRollbackPcmpMisBatch } from '../../hooks/useRollbackPcmpMisBatch';
import type { PcmpMisRollbackResult } from '../../types/pcmpMis.types';
import { PCMP_CHANNELS, type PcmpChannel } from '../../types/pcmpProposal.types';
import { PcmpCommentField } from '../PcmpMarketingActions/PcmpCommentField';
import { PcmpEditDialog } from '../PcmpProposalEdit/PcmpEditDialog';
import { PcmpOutcomeDialog } from '../PcmpBatch/PcmpOutcomeDialog';

const rollbackFormSchema = z.object({
  target: z.enum(PCMP_CHANNELS),
  comment: z.string().trim().min(1, 'A comment is required.').max(PCMP_MKT_COMMENT_MAX),
});

type RollbackFormValues = z.infer<typeof rollbackFormSchema>;

export interface PcmpMisRollbackTarget {
  batchNo: number;
  /** Pre-selected target: the batch's own channel. */
  channel: PcmpChannel | null;
  proposalIds: number[];
  amount: number;
}

interface PcmpMisRollbackDialogProps {
  target: PcmpMisRollbackTarget;
  refNoOf?: ((proposalId: number) => string | undefined) | undefined;
  onRolledBack: () => void;
  onClose: () => void;
}

/**
 * Legacy MisRouteBatchRollback: take staged proposals out of the batch, back
 * to Pre-Batch; the payment source follows the chosen side.
 */
export function PcmpMisRollbackDialog({
  target,
  refNoOf,
  onRolledBack,
  onClose,
}: PcmpMisRollbackDialogProps) {
  const [outcome, setOutcome] = useState<PcmpMisRollbackResult | null>(null);
  const form = useForm<RollbackFormValues>({
    resolver: zodResolver(rollbackFormSchema),
    defaultValues: { target: target.channel ?? 'ACCOUNTS', comment: '' },
  });
  const { mutate, isPending, error } = useRollbackPcmpMisBatch(target.batchNo, (result) => {
    onRolledBack();
    if (result.blocked.length > 0) setOutcome(result);
    else onClose();
  });

  const onSubmit = form.handleSubmit((values) =>
    mutate({
      proposalIds: target.proposalIds,
      targetPaymentSource: values.target,
      comment: values.comment,
    }),
  );

  if (outcome) {
    return (
      <PcmpOutcomeDialog
        title="Rolled back"
        summary={`${formatCount(outcome.rolledBack.length, 'proposal')} moved to ${
          PCMP_PRE_BATCH_LABELS[outcome.paymentSource]
        }. The ones below could not be rolled back.`}
        skipped={outcome.blocked}
        refNoOf={refNoOf}
        onClose={onClose}
      />
    );
  }

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title={`Roll back from Batch #${target.batchNo}`}
        description={`${formatCount(target.proposalIds.length, 'proposal')} · ${formatTaka(target.amount)}`}
        icon={Undo2}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel="Roll back"
        submitVariant="destructive"
        pendingLabel="Rolling back..."
      >
        <FormField
          control={form.control}
          name="target"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Roll back to <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <RadioGroup value={field.value} onValueChange={field.onChange}>
                  {PCMP_CHANNELS.map((channel) => (
                    <div
                      key={channel}
                      className="flex items-center gap-3 rounded-lg border px-3 py-2.5"
                    >
                      <RadioGroupItem id={`pcmp-rollback-${channel}`} value={channel} />
                      <Label htmlFor={`pcmp-rollback-${channel}`} className="font-normal">
                        {PCMP_PRE_BATCH_LABELS[channel]}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </FormControl>
              <p className="text-xs text-muted-foreground">
                The payment source changes to match, and the proposals wait on that staging tab.
              </p>
              <FormMessage />
            </FormItem>
          )}
        />
        <PcmpCommentField
          control={form.control}
          name="comment"
          label="Comment"
          required
          placeholder="Why these proposals leave the batch"
        />
      </PcmpEditDialog>
    </Form>
  );
}
